/*
  A card page's header: the game and set headers carried down to one card (Robert,
  2026-10-20: the set and card pages as modern as the game page, "if there's any other
  information that should be added … let's try it"). CardView and CardGradeView both show
  it, and it fetches for itself so the two cannot drift.

  One card: the card's art on the left (a Front/Back switch when the catalogue has the
  reverse), and on the right the card's name, its facts as pills, and its figures:

    - cards graded, the big gold number;
    - its top grade and the population at it ("pop 1"), which is what a collector asks first;
    - how many graded a 10, and what share;
    - where it ranks among the set's graded cards;
    - the grade mix bar;
    - under it, the "Graded in" chart for this card's product
      (docs/specs/pop-report-followups.md R16b, D14), with no-language gradings as English;
    - its average subgrades.

  **Two sources, on purpose.** The counts (total, mix, top grade, rank) come from the set
  endpoint's row for this card, which carries every grade count. The gradings search that
  the table below uses returns at most 200 gradings, and a popular card has more (Nymble in
  MEGA: Inferno X has 834), so counting those would understate it. The averages need the
  subgrades, which only the gradings carry, so they are taken over the gradings in hand and
  say how many that is. Both requests are the ones the views already make, so RTK serves
  them from one fetch each.

  The chart's product id is the catalogue product's `id`, which both feeds carry (the set
  row is a GameProduct, and so is each grading's cardInfo). The set row comes first, since
  it is the row the counts above are read from.
*/

import { useState } from 'react';
import { Link } from 'react-router-dom';

import GradeMixBar from 'components/GradeMixBar';
import GradedLanguagesChart from 'components/GradedLanguagesChart';
import {
  HEADER_CARD,
  HEADER_EYEBROW,
  HEADER_GLOW,
  HEADER_TITLE,
  HeaderFigure,
  HeaderPill,
  HeaderRule,
} from 'components/PageHeaderCard';
import { CONTROL_LABEL, FOCUS_RING, LINK } from 'components/styles';
import {
  cardSummary,
  formatCount,
  formatShare,
  formatSubgrade,
  ordinal,
  rankByCount,
  tallyGradeCounts,
  tallyTens,
  tallyTotal,
  topGradeFromCounts,
} from 'lib';
import { sanitizeURIPathSegment } from 'helpers/routing';
import { cleanSetName } from 'lib/setName';
import {
  useGetGamesQuery,
  useGetProductsWithCountsQuery,
  useSearchGradingsQuery,
} from 'services/pcgPublicV2';

/* CardView's own limit, so this is the same request and RTK shares it. */
export const CARD_GRADINGS_LIMIT = 200;

interface CardHeaderProps {
  gameCode: string;
  setName: string;
  number: string;
}

const MESSAGE = 'text-center text-sm italic text-pcg-textMuted';

const sameNumber = (a: string | null | undefined, b: string) =>
  (a ?? '').trim().toLowerCase() === b.trim().toLowerCase();

const yearOf = (releaseDate: string | null | undefined): string | null => {
  const match = /^\d{4}/.exec(releaseDate ?? '');
  return match ? match[0] : null;
};

const CardHeader = ({ gameCode, setName, number }: CardHeaderProps) => {
  const [side, setSide] = useState<'front' | 'back'>('front');

  const { data: games = [] } = useGetGamesQuery();
  const game = games.find(g => g.gameCode.toLowerCase() === gameCode.toLowerCase());

  const gradingsQuery = useSearchGradingsQuery({
    gameCode,
    setName,
    cardNumber: number,
    offset: 0,
    limit: CARD_GRADINGS_LIMIT,
  });
  const { currentData: products } = useGetProductsWithCountsQuery(
    { gameCode, setName },
    { skip: !gameCode || !setName }
  );

  const gradings = gradingsQuery.currentData?.data ?? [];
  const gradingsTotal = gradingsQuery.currentData?.pagination?.total ?? gradings.length;
  const cardInfo = gradings[0]?.cardInfo ?? null;
  const setCards: any[] = products ?? [];
  const product = setCards.find(row => sameNumber(row.number, number)) ?? null;

  const name = cardInfo?.cardName || product?.cardName || product?.title || '';
  const image = cardInfo?.image || product?.image || '';
  const reverse = cardInfo?.reverseImage || product?.reverseImage || '';
  const rarity = cardInfo?.rarity || product?.rarity || '';
  const finish = product?.finish || '';
  const language = product?.language || '';
  const year = yearOf(cardInfo?.releaseDate || product?.releaseDate);
  const productId: number | undefined =
    typeof product?.id === 'number' ? product.id : typeof cardInfo?.id === 'number' ? cardInfo.id : undefined;

  /*
    The counts: the set row's, or — when the card is not in the set feed — the gradings',
    but only if every grading is in hand.
  */
  const fromGradings = cardSummary(gradings);
  const gradingsComplete = gradingsQuery.currentData !== undefined && gradings.length >= gradingsTotal;
  const tally = product
    ? tallyGradeCounts([product])
    : gradingsComplete
      ? fromGradings.tally
      : null;
  const graded = product
    ? product.cardsGraded ?? 0
    : gradingsComplete
      ? fromGradings.graded
      : null;
  const topGrade = product ? topGradeFromCounts(product.finalGradeCounts) : gradingsComplete ? fromGradings.topGrade : null;
  const tens = tally ? tallyTens(tally) : 0;
  const tallied = tally ? tallyTotal(tally) : 0;
  const rank = product
    ? rankByCount(setCards, row => sameNumber(row.number, number), row => row.cardsGraded)
    : null;
  const subgrades = fromGradings.subgrades;

  const loading = gradingsQuery.isFetching && !cardInfo && !product;
  const notFound = !loading && !gradingsQuery.error && !cardInfo && !product && products !== undefined;
  const shown = side === 'back' && reverse ? reverse : image;

  return (
    <section
      aria-labelledby="card-heading"
      className={`${HEADER_CARD} grid gap-6 md:grid-cols-[minmax(0,300px)_minmax(0,1fr)] lg:gap-8`}
      style={HEADER_GLOW}
    >
      <div className="flex flex-col items-center gap-3">
        <div className="flex min-h-[220px] w-full items-center justify-center rounded-xl bg-pcg-raised/50 p-4">
          {loading ? (
            <p role="status" className={MESSAGE}>Loading…</p>
          ) : gradingsQuery.error && !product ? (
            <p className={MESSAGE}>Card details are unavailable right now.</p>
          ) : notFound ? (
            <p className={MESSAGE}>Card not found.</p>
          ) : shown ? (
            <img
              src={shown}
              alt={`${name || 'Card'}${side === 'back' ? ', back' : ''}`}
              className="h-auto w-full max-w-[260px] rounded-lg shadow-xl"
            />
          ) : null}
        </div>
        {reverse && image ? (
          <div role="group" aria-label="Card side" className="inline-flex rounded-full border border-pcg-border p-0.5">
            {(['front', 'back'] as const).map(value => (
              <button
                key={value}
                type="button"
                aria-pressed={side === value}
                onClick={() => setSide(value)}
                className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${FOCUS_RING} ${
                  side === value ? 'bg-pcg-gold text-black' : 'text-pcg-textMuted hover:text-pcg-text'
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="min-w-0 md:self-center">
        <p className={HEADER_EYEBROW}>
          {game ? (
            <Link to={`/${gameCode}`} className={`${LINK} no-underline hover:underline`}>
              {game.name}
            </Link>
          ) : (
            'Population Report'
          )}
          {' · '}
          <Link to={`/${gameCode}/${sanitizeURIPathSegment(setName)}`} className={`${LINK} no-underline hover:underline`}>
            {cleanSetName(setName)}
          </Link>
        </p>
        <h1 id="card-heading" className={`mt-2 ${HEADER_TITLE}`}>
          {name || `Card #${number}`}
        </h1>
        <p className="mt-3 flex flex-wrap items-center gap-2">
          <HeaderPill>#{number}</HeaderPill>
          {rarity ? <HeaderPill>{rarity}</HeaderPill> : null}
          {finish
            ? finish.split(',').map((part: string) => part.trim()).filter(Boolean).map((part: string) => (
                <HeaderPill key={part}>{part}</HeaderPill>
              ))
            : null}
          {language ? <HeaderPill>{language}</HeaderPill> : null}
          {year ? <HeaderPill>Released {year}</HeaderPill> : null}
        </p>
        <HeaderRule />

        <dl className="mt-5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          {graded !== null ? <HeaderFigure label="Cards graded" value={formatCount(graded)} accent /> : null}
          {topGrade ? (
            <HeaderFigure label="Top grade" value={topGrade.label} note={`Pop ${formatCount(topGrade.pop)}`} />
          ) : null}
          {tally && tallied > 0 ? (
            <HeaderFigure
              label="Graded 10"
              value={formatCount(tens)}
              note={`${formatShare((tens / tallied) * 100)} of its grades`}
            />
          ) : null}
          {rank ? (
            <HeaderFigure
              label="Most graded in set"
              value={ordinal(rank.rank)}
              note={`of ${formatCount(rank.of)} graded cards`}
            />
          ) : null}
        </dl>

        {tally ? (
          <div className="mt-5">
            <GradeMixBar tally={tally} />
          </div>
        ) : null}

        {/* Hidden until an id is known, and while loading, on error or with nothing graded. */}
        <GradedLanguagesChart productId={productId} className="mt-5" />

        {subgrades ? (
          <div className="mt-5">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span className={CONTROL_LABEL}>Average subgrades</span>
              <span className="text-xs text-pcg-textMuted">
                {/* Authentics carry no subgrades, so "over" is usually fewer than the gradings. */}
                {gradingsComplete
                  ? `Over ${formatCount(subgrades.over)} ${subgrades.over === 1 ? 'grading' : 'gradings'} with subgrades`
                  : `Over ${formatCount(subgrades.over)} with subgrades, from ${formatCount(gradings.length)} of ${formatCount(gradingsTotal)} gradings`}
              </span>
            </div>
            <dl className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              {(
                [
                  ['Centering', subgrades.centering],
                  ['Corners', subgrades.corners],
                  ['Edges', subgrades.edges],
                  ['Surface', subgrades.surface],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="rounded-lg border border-pcg-borderSoft px-3 py-2">
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-pcg-textMuted">{label}</dt>
                  <dd className="text-lg font-bold tabular-nums text-pcg-text">{formatSubgrade(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default CardHeader;
